import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["email", "phone", "firstName", "lastName", "dropdown"]
  static values = { url: String }

  connect() {
    this.selectedIndex = -1
    this.debounceTimer = null
    this.abortController = null
    this._handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this._handleClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this._handleClickOutside)
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    if (this.abortController) this.abortController.abort()
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  search(event) {
    const query = event.target.value.trim()
    this.activeInput = event.target

    if (query.length < 2) {
      this.hideDropdown()
      return
    }

    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.fetchResults(query), 250)
  }

  async fetchResults(query) {
    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()

    try {
      const response = await fetch(`${this.urlValue}?query=${encodeURIComponent(query)}`, {
        headers: { "Accept": "application/json" },
        signal: this.abortController.signal
      })
      if (!response.ok) { this.hideDropdown(); return }

      const results = await response.json()
      this.renderDropdown(results, query)
    } catch (error) {
      if (error.name !== "AbortError") this.hideDropdown()
    }
  }

  renderDropdown(results, query) {
    if (results.length === 0) {
      this.hideDropdown()
      return
    }

    this.selectedIndex = -1

    let html = ""
    results.forEach((user, index) => {
      const name = [user.first_name, user.last_name].filter(Boolean).join(" ")
      const details = [user.email, user.phone_number].filter(Boolean).join(" · ")
      const highlightedName = this.highlightMatch(name, query)
      const highlightedDetails = this.highlightMatch(details, query)

      html += `<div class="px-3 py-2 cursor-pointer hover:bg-base-200 ${index === this.selectedIndex ? 'bg-base-200' : ''} text-sm border-b border-base-200 last:border-0"
                   data-action="click->customer-autofill#select"
                   data-first-name="${this.escapeAttr(user.first_name || '')}"
                   data-last-name="${this.escapeAttr(user.last_name || '')}"
                   data-email="${this.escapeAttr(user.email || '')}"
                   data-phone="${this.escapeAttr(user.phone_number || '')}">
                <div class="font-medium">${highlightedName || '<span class="opacity-40">—</span>'}</div>
                <div class="text-xs opacity-60">${highlightedDetails || '—'}</div>
              </div>`
    })

    this.dropdownTarget.innerHTML = html
    this.dropdownTarget.classList.remove("hidden")
    this.positionDropdown()
  }

  positionDropdown() {
    if (!this.activeInput) return
    const inputRect = this.activeInput.getBoundingClientRect()
    const containerRect = this.element.getBoundingClientRect()

    this.dropdownTarget.style.left = `${inputRect.left - containerRect.left}px`
    this.dropdownTarget.style.top = `${inputRect.bottom - containerRect.top + 4}px`
    this.dropdownTarget.style.width = `${inputRect.width}px`
  }

  highlightMatch(text, query) {
    if (!text) return ""
    const regex = new RegExp(`(${this.escapeRegex(query)})`, "gi")
    return this.escapeHtml(text).replace(regex, '<strong class="text-primary">$1</strong>')
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  }

  escapeHtml(string) {
    const div = document.createElement("div")
    div.textContent = string
    return div.innerHTML
  }

  escapeAttr(string) {
    return String(string)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }

  select(event) {
    const item = event.currentTarget
    this.emailTarget.value = item.dataset.email
    this.phoneTarget.value = item.dataset.phone
    this.firstNameTarget.value = item.dataset.firstName
    this.lastNameTarget.value = item.dataset.lastName
    this.hideDropdown()
  }

  hideDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add("hidden")
    }
    this.selectedIndex = -1
  }

  handleKeydown(event) {
    if (!this.hasDropdownTarget || this.dropdownTarget.classList.contains("hidden")) {
      return
    }

    const items = this.dropdownTarget.querySelectorAll("[data-action]")

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
        this.updateSelection(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.updateSelection(items)
        break
      case "Enter":
        event.preventDefault()
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          items[this.selectedIndex].click()
        }
        break
      case "Escape":
        this.hideDropdown()
        break
    }
  }

  updateSelection(items) {
    items.forEach((item, index) => {
      item.classList.toggle("bg-base-200", index === this.selectedIndex)
    })
  }
}
